<template>
  <Modal
    :open="contentModalOpen"
    @close-modal="contentModalOpen = false"
    customHeight="h-100"
    customWidth="lg:w-240"
  >
    <template v-slot:modal-header>
      <div class="flex items-center justify-between">
        <div class="flex flex-row items-center">
          <div
            class="mr-4 flex h-8 w-8 items-center justify-center rounded-md bg-custom-navy bg-opacity-20 p-1"
          >
            <Icon name="Rss" class="h-6 w-6 text-strongest" :stroke-width="2" />
          </div>
          <div class="font-bold text-strongest">Latest Updates</div>
        </div>
      </div>
    </template>

    <template v-slot:modal-body>
      <iframe :src="selectedContent" width="100%" height="600" style="border: none"></iframe>
    </template>
    <template #modal-footer>
      <div class="flex justify-end gap-2">
        <Button @click="contentModalOpen = false" variant="paper-primary" size="sm" class="">
          <template #label>Close</template>
        </Button>
      </div>
    </template>
  </Modal>
  <slot />
  <div class="flex shrink-0 flex-start mb-16 overflow-x-auto space-x-6 max-w-[1180px]">
    <!-- LEFT COLUMN (1/5) -->
    <div
      class="min-w-[17.625rem] flex-1 flex-col gap-1 shrink-0 justify-between p-3 bg-container-bg rounded-2xl space-y-8"
    >
      <LatestUpdateCard
        :card="latestUpdates[0]"
        @click="
          (((contentModalOpen = true), (selectedContent = latestUpdates[0].video_url)),
          handleInvActivityLog(latestUpdates[0]))
        "
      />
      <LatestUpdateCard
        :card="latestUpdates[1]"
        @click="
          (((contentModalOpen = true), (selectedContent = latestUpdates[1].video_url)),
          handleInvActivityLog(latestUpdates[1]))
        "
      />
    </div>

    <!-- CENTER COLUMN (3/5) -->
    <div
      class="min-w-[540px] flex-[2] items-center shrink-0 justify-center p-3 bg-container-bg rounded-2xl"
    >
      <LatestUpdateCard
        :card="latestUpdates[2]"
        @click="
          (((contentModalOpen = true), (selectedContent = latestUpdates[2].video_url)),
          handleInvActivityLog(latestUpdates[2]))
        "
      />
      <div class="flex justify-center w-full">
        <Button
          @click="
            (((contentModalOpen = true), (selectedContent = latestUpdates[2].video_url)),
            handleInvActivityLog(latestUpdates[2]))
          "
          icon
          label
          size="lg"
          variant="always-primary-neo"
          class="mt-6.5"
        >
          <template #icon>
            <Icon name="TvMinimalPlay" :stroke-width="2" class="h-6 w-6" />
          </template>
          <template #label>Watch Video</template>
        </Button>
      </div>
    </div>

    <!-- RIGHT COLUMN (1/5) -->
    <div
      class="min-w-[17.625rem] flex-1 flex-col gap-8 shrink-0 justify-between p-3 bg-container-bg rounded-2xl space-y-8"
    >
      <LatestUpdateCard
        :card="latestUpdates[3]"
        @click="
          (((contentModalOpen = true), (selectedContent = latestUpdates[3].video_url)),
          handleInvActivityLog(latestUpdates[3]))
        "
      />
      <LatestUpdateCard
        :card="latestUpdates[4]"
        @click="
          (((contentModalOpen = true), (selectedContent = latestUpdates[4].video_url)),
          handleInvActivityLog(latestUpdates[4]))
        "
      />
    </div>
  </div>
</template>

<script setup>
import IconPHXLogo from "@/components/icons/IconPHXLogo.vue";

import FastestGrowing from "@/assets/images/FastestGrowing.webp";
import WebinarPresentation from "@/assets/images/WebinarPresentation.png";
import FieldUpdates from "@/assets/images/FieldUpdates.jpg";
import FixedMonthlyReturns from "@/assets/images/FixedMonthlyReturns.png";
import Accreditation from "@/assets/images/Accreditation.png";

import LatestUpdateCard from "./LatestUpdateCard.vue";
import Modal from "@/components/Modal.vue";
import { ref } from "vue";
import { Button, Icon } from "@/components";
import { useDataStore, useCrudStore } from "@/stores";

const contentModalOpen = ref(false);
const selectedContent = ref(null);
const dataStore = useDataStore();
const crudStore = useCrudStore();

const latestUpdates = [
  {
    id: 1,
    type: "video",
    video_url: "https://phoenixenergy.com/webinar-summary/",
    image: WebinarPresentation,
    title: "Review our Bond Offerings",
    subtext:
      "Watch a quick 10-minute video from Matt Willer, Managing Director of Capital Markets, to learn more about the bond offerings available through Phoenix Energy.",
  },
  {
    id: 2,
    type: "standard",
    video_url: "https://www.canva.com/design/DAGcM87fceo/fSpWJFkH_poOlS1JyGpRkQ/view?embed",
    image: FixedMonthlyReturns,
    title: "Dive Deeper Into the Phoenix Energy Approach",
    subtext:
      "Access our Bondholder Guide to learn about our fixed monthly return bond offerings. You can read about our business model and strategic approach to investing at your own pace before investing. ",
  },
  {
    id: 3,
    type: "video",
    video_url: "https://www.youtube.com/embed/nNyLz1Vxo-M",
    image: FieldUpdates,
    title: "Updates from the Field",
    subtext:
      "Learn about our operations in the Williston Basin of North Dakota and Montana. In this video, Phoenix Energy CEO, Adam Ferrari, shares a field update from the Nystuen pad in North Dakota. Five 4-mile lateral wells were drilled from a single surface location - making this the first time this has been done in the state.",
  },
  {
    id: 4,
    type: "standard",
    video_url: "https://phoenixenergy.com/accreditation/",
    image: Accreditation,
    title: "Understanding Investor Accreditation",
    subtext:
      "To participate in our Private Placement Offerings, investors must qualify as “accredited investors” as defined by Rule 501 of the Regulation D. Find out what that means and if you qualify. ",
  },
  {
    id: 5,
    type: "standard",
    video_url:
      "https://phoenixenergy.com/post/2025/04/10/phoenix-energy-ranks-top-50-on-financial-times-fastest-growing-companies-list/",
    image: FastestGrowing,
    title: "Phoenix Energy Named on Fastest Growing Companies List",
    subtext:
      "Phoenix Energy was named to the Financial Times list of “The Americas’ Fastest-Growing Companies 2025.” The company ranked #33 out of 300 ranked organizations and was selected from thousands of applicants throughout North and South America.",
  },
];

async function handleInvActivityLog(card) {
  // console.log("Logging investor activity for card:", card);
  let log = {
    step: "Dashboard - Latest Updates",
    substep: card.title,
    status: `Engaged with ${card.type} content`,
    message: `Engaged with ${card.type} content`,
    url: card.video_url,
  };

  log = dataStore.setDefaultFields(log, "investoractivity");
  crudStore.insert("investoractivity", [log]);
}
</script>
